/*
 * 1DS JS SDK QoS plugin, 3.1.9
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
/**
* QosPlugin.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2018
* File containing QoS plugin.
*/
import { __extendsFn as __extends } from "@microsoft/applicationinsights-shims";
import { EventLatency, isValueAssigned, extend, createGuid, LoggingSeverity, _ExtendedInternalMessageId, setProcessTelemetryTimings, objForEachKey, isObject, isFunction, isUndefined } from "@microsoft/1ds-core-js";
import dynamicProto from "@microsoft/dynamicproto-js";
import OutgoingQosEvent from "./events/OutgoingQosEvent";
import IncomingQosEvent from "./events/IncomingQosEvent";
import { _removeNonObjectsAndInvalidElements } from "./common/Utils";
import { _getMetaData, _collectMetaTags, _getPageName, _getUri } from "./common/DataCollector";
import { AjaxPlugin } from "@microsoft/applicationinsights-dependencies-js";
import { RemoteDependencyData } from "@microsoft/applicationinsights-common";
import Id from "./Id";
var MS_CV = "MS-CV";
var cfgDistributedTracingMode = "distributedTracingMode";
var QosPlugin = /** @class */function (_super) {
  __extends(QosPlugin, _super);
  /**
   * @constructor
   */
  function QosPlugin() {
    var _this = _super.call(this) || this;
    _this.identifier = "QosPlugin";
    _this.version = '3.1.9';
    var _cvPlugin;
    var _outgoingQosEvent;
    var _incomingQosEvent;
    var _metaTags = {
      market: "",
      serverImpressionGuid: ""
    };
    var theConfig = {};
    _outgoingQosEvent = new OutgoingQosEvent();
    _incomingQosEvent = new IncomingQosEvent();
    dynamicProto(QosPlugin, _this, function (_self, _base) {
      /**
       * @ignore
       * Merge passed in configuration with default configuration
       * @param overrideConfig - configuration
       */
      function _mergeConfig(overrideConfig) {
        var defaultConfig = {
          enableCorsCorrelation: false,
          correlationHeaderExcludedDomains: [],
          correlationHeaderExcludePatterns: null,
          disableCorrelationHeaders: false,
          // Unlimited autocollection
          maxAjaxCallsPerView: -1,
          disableAjaxTracking: false,
          disableFetchTracking: false,
          enableCvHeaders: false,
          appId: undefined,
          callback: {
            pageName: null,
            id: null
          },
          autoCapture: {
            msTags: true
          },
          // overrideValues SDK should use instead of collecting automatically
          coreData: {
            requestUri: "",
            pageName: ""
          }
        };
        var attributesThatAreObjectsInConfig = [];
        objForEachKey(defaultConfig, function (attribute, value) {
          if (value && isObject(value)) {
            attributesThatAreObjectsInConfig.push(attribute);
          }
        });
        // delete attributes that should be object and
        // delete properties that are null, undefined, ''
        _removeNonObjectsAndInvalidElements(overrideConfig, attributesThatAreObjectsInConfig);
        return extend(true, defaultConfig, overrideConfig);
      }
      /**
       * Override of parent method, executed when auto capture is enabled for XMLHttpRequest or Fetch API
       * @param ajaxData - Outgoing Ajax call.
       * @param input - Fetch API request input
       * @param init - Fetch API initiator
       * @param xhr - XMLHttpRequest
       */
      _self.includeCorrelationHeaders = function (ajaxData, input, init, xhr) {
        var dependencyRequest = _base.includeCorrelationHeaders(ajaxData, input, init, xhr);
        if (theConfig.enableCvHeaders) {
          var cv = _self.getCv();
          if (cv && (input || xhr)) {
            // Only try to increment if we have a fetch or xhr
            cv.increment();
            var cvValue = cv.getValue();
            if (input) {
              // Fetch
              if (!dependencyRequest) {
                // reuse the past init if available and no dependencyRequest was returned, so that
                // we correctly reference the init.headers
                dependencyRequest = init || {};
              }
              // dependencyRequest headers overrides original request headers
              // so, if they exist use only them, otherwise use request's because they should have been applied in the first place
              // not using original request headers will result in them being lost
              if (!dependencyRequest.headers || !isFunction(dependencyRequest.headers.append)) {
                dependencyRequest.headers = new Headers(dependencyRequest.headers || (input instanceof Request ? input.headers || {} : {}));
              }
              dependencyRequest.headers.append(MS_CV, cvValue);
            } else {
              // XHR
              dependencyRequest.setRequestHeader(MS_CV, cvValue);
            }
          }
        }
        return dependencyRequest;
      };
      /**
       * Initialize the Qos plugin
       * @param config - The plugin configuration.
       * @param core - Core reference.
       * @param extensions - All other extensions
       */
      _self.initialize = function (coreConfig, core, extensions) {
        var extConfig = coreConfig.extensionConfig = coreConfig.extensionConfig || [];
        extConfig[_self.identifier] = extConfig[_self.identifier] || {};
        theConfig = _self._qosConfig = _mergeConfig(extConfig[_self.identifier]);
        if (isUndefined(theConfig[cfgDistributedTracingMode])) {
          // App Insights default is now AI_AND_W3C, so resetting to AI by default so the id is unchanged
          theConfig[cfgDistributedTracingMode] = 0;
        }
        // Update parent config
        extConfig[AjaxPlugin.identifier] = theConfig;
        _base.initialize(coreConfig, core, extensions);
        var metaTags = _collectMetaTags(_self._qosConfig);
        _metaTags.market = _getMetaData(metaTags, theConfig.coreData, "market");
        _metaTags.serverImpressionGuid = _getMetaData(metaTags, theConfig.coreData, "serverImpressionGuid");
        // Iterate other loaded extensions
        var correlationVectorExtension = null;
        var webAnalyticsExtension = null;
        for (var i = 0; i < extensions.length; i++) {
          if (extensions[i].identifier === "CorrelationVectorPlugin") {
            correlationVectorExtension = extensions[i];
          } else if (extensions[i].identifier === "WebAnalyticsPlugin") {
            webAnalyticsExtension = extensions[i];
          }
        }
        // Use WebAnalytics Id if available
        if (webAnalyticsExtension && webAnalyticsExtension.id) {
          _self.id = webAnalyticsExtension.id;
        } else {
          _self.id = new Id();
        }
        if (theConfig.enableCvHeaders) {
          if (correlationVectorExtension) {
            theConfig.enableCvHeaders = true;
            _cvPlugin = correlationVectorExtension;
          } else {
            _self.diagLog().throwInternal(LoggingSeverity.WARNING, _ExtendedInternalMessageId.CVPluginNotAvailable, "Enable cV headers it is set to \"true\" in config.  However, cv plugin is not available. Disabling adding Cv in dependency requests ");
            theConfig.enableCvHeaders = false;
          }
        }
      };
      /**
       * Process a given event.
       * @param env - This is the current event being reported
       * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
       * can optionally use this to access the current core instance or define / pass additional information
       * to later plugins (vs appending items to the telemetry item)
       */
      _self.processTelemetry = function (evt, itemCtx) {
        setProcessTelemetryTimings(evt, _self.identifier);
        var event = evt;
        switch (event.baseType) {
          case RemoteDependencyData.dataType:
            if (event.name !== "Ms.Qos.OutgoingServiceRequest") {
              event.name = "Ms.Web.OutgoingRequest";
              event.baseData = event.baseData || {};
              event.baseData.properties = event.baseData.properties || {};
              event.baseData.properties["parentId"] = event.baseData.properties["parentId"] || _self.id.getLastPageViewId();
              // Remove extra AI properties
              delete event.baseData["method"];
            }
            event.latency = EventLatency.Normal;
            break;
          default:
            break;
        }
        _self.processNext(event, itemCtx);
      };
      /**
      * Track DependencyData event
      * @param dependency -DependencyData event
      * @param properties - DependencyData properties
      */
      _self.trackDependencyData = function (dependency, properties) {
        _self.trackDependencyDataInternal(dependency, properties);
      };
      /**
      * Track DependencyData event internal (Called by automatic tracking scenarios)
      * @param dependency -DependencyData event
      * @param properties - DependencyData properties
      */
      _self.trackDependencyDataInternal = function (dependency, properties, systemProperties) {
        // Part A
        var ext = systemProperties || {};
        if (isValueAssigned(dependency.cV)) {
          ext["mscv"] = {};
          ext["mscv"]["cV"] = dependency.cV;
        }
        dependency.id = theConfig.callback && theConfig.callback.id ? theConfig.callback.id() : createGuid();
        // Part C
        properties = properties || {};
        properties.pageName = isValueAssigned(properties.pageName) ? properties.pageName : _getPageName(_self._qosConfig);
        properties.uri = isValueAssigned(properties.uri) ? properties.uri : _getUri(_self._qosConfig);
        properties.market = isValueAssigned(properties.market) ? properties.market : _metaTags.market;
        properties.serverImpressionGuid = isValueAssigned(properties.serverImpressionGuid) ? properties.serverImpressionGuid : _metaTags.serverImpressionGuid;
        _base.trackDependencyDataInternal(dependency, properties, ext);
      };
      /**
       * Track Service QoS event
       * @param eventData - IExtendedTelemetryItem event
       * @param properties - Custom event properties (part C)
       */
      function _trackServiceQos(event, customProperties) {
        _addCustomProperties(event, customProperties);
        _self.core.track(event);
      }
      /**
       * Track OutgoingServiceRequest event
       * @param eventData - OutgoingServiceRequest event data
       * @param properties - Custom event properties (part C)
       */
      _self.trackServiceOutgoingQos = function (eventData, customProperties) {
        var event = {
          name: "Ms.Qos.OutgoingServiceRequest",
          baseType: "RemoteDependencyData",
          ext: {},
          baseData: {},
          data: {},
          latency: EventLatency.RealTime
        };
        _outgoingQosEvent._setQosProperties(event, eventData);
        _addCustomProperties(event, customProperties);
        _trackServiceQos(event);
      };
      /**
       * Track IncomingServiceRequest event
       * @param eventData - IncomingServiceRequest event data
       */
      _self.trackServiceIncomingQos = function (eventData, customProperties) {
        var event = {
          name: "Ms.Qos.IncomingServiceRequest",
          baseType: "RequestData",
          ext: {},
          baseData: {},
          data: {},
          latency: EventLatency.RealTime
        };
        _incomingQosEvent._setQosProperties(event, eventData);
        _addCustomProperties(event, customProperties);
        _trackServiceQos(event);
      };
      _self.getCv = function () {
        if (_cvPlugin) {
          return _cvPlugin.getCv();
        }
        return null;
      };
      function _addCustomProperties(event, customProperties) {
        // Add extra Part C
        if (isValueAssigned(customProperties)) {
          objForEachKey(customProperties, function (prop, value) {
            event.data[prop] = value;
          });
        }
      }
    });
    return _this;
  }
  // Removed Stub for QosPlugin.prototype.includeCorrelationHeaders.
  // Removed Stub for QosPlugin.prototype.initialize.
  // Removed Stub for QosPlugin.prototype.processTelemetry.
  // Removed Stub for QosPlugin.prototype.trackDependencyData.
  // Removed Stub for QosPlugin.prototype.trackServiceOutgoingQos.
  // Removed Stub for QosPlugin.prototype.trackServiceIncomingQos.
  // Removed Stub for QosPlugin.prototype.getCv.
  // Removed Stub for QosPlugin.prototype.trackDependencyDataInternal.
  return QosPlugin;
}(AjaxPlugin);
export default QosPlugin;